import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Loader2, MessageCircle, Sparkles, Heart, Home, BookOpen, Flower, MoreHorizontal, Users, User, LogOut, Stars } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';

import LoveGardenSimple from './LoveGardenSimple';

const AppPreview = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [journalView, setJournalView] = useState('freeform');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [journalText, setJournalText] = useState('');
  const [isListening, setIsListening] = useState(false);
  
  // Enhanced Love Garden states
  const [gardenView, setGardenView] = useState('map'); // map, playing, complete
  const [currentLevel, setCurrentLevel] = useState(1);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [gameBoard, setGameBoard] = useState([]);
  const [score, setScore] = useState(0);
  const [movesLeft, setMovesLeft] = useState(15);
  const [heartsMatched, setHeartsMatched] = useState(0);
  const [waterMatched, setWaterMatched] = useState(0);
  const [loveCoinsBalance, setLoveCoinsBalance] = useState(247);
  const [userBadges, setUserBadges] = useState(['Bloom Badge 🌸']);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  
  // Vision Board demo states
  const [visionBoardView, setVisionBoardView] = useState('gallery'); // gallery, manual, ai_assisted
  const [savedBoards, setSavedBoards] = useState([]);
  const [currentBoard, setCurrentBoard] = useState({
    title: '',
    category: 'general',
    elements: []
  });
  const [boardElements, setBoardElements] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [gameState, setGameState] = useState('home');

  const handleReflectNow = (prompt, category, description) => {
    setSelectedPrompt({ prompt, category, description });
    setShowJournalModal(true);
    setJournalText('');
  };

  const handleVoiceInput = () => {
    setIsListening(!isListening);
    // Simulate voice input
    setTimeout(() => {
      if (!isListening) {
        setJournalText(prev => prev + " I'm feeling grateful for this moment of reflection...");
        setIsListening(false);
      }
    }, 2000);
  };

  const navItems = [
    { id: 'home', icon: Home, label: 'Home' },
    { id: 'journal', icon: BookOpen, label: 'Journaling' },
    { id: 'companion', icon: Bot, label: 'Companion' },
    { id: 'garden', icon: Flower, label: 'Garden' },
    { id: 'community', icon: MessageCircle, label: 'Community' },
    { id: 'more', icon: MoreHorizontal, label: 'More' }
  ];

  // Vision Board functions
  const createNewBoard = (mode) => {
    setVisionBoardView(mode);
    setCurrentBoard({
      title: '',
      category: 'general',
      elements: []
    });
    setBoardElements([]);
  };

  const addImageToBoard = (imageUrl) => {
    const newElement = {
      id: Date.now().toString(),
      type: 'image',
      content: imageUrl,
      x: Math.random() * 60 + 10,
      y: Math.random() * 60 + 10,
      width: 25,
      height: 20
    };
    setBoardElements([...boardElements, newElement]);
    setShowImagePicker(false);
  };

  const addTextToBoard = () => {
    const newElement = {
      id: Date.now().toString(),
      type: 'text',
      content: 'Your affirmation here',
      x: Math.random() * 50 + 10,
      y: Math.random() * 70 + 10,
      width: 40,
      height: 8,
      style: { fontSize: '20px', fontWeight: 'bold', color: '#f43f5e' }
    };
    setBoardElements([...boardElements, newElement]);
  };

  const saveVisionBoard = () => {
    if (!currentBoard.title) {
      alert('Please enter a title for your vision board');
      return;
    }

    const newBoard = {
      id: Date.now().toString(),
      title: currentBoard.title,
      category: currentBoard.category,
      elements: boardElements,
      created: new Date().toLocaleDateString(),
      images: boardElements.filter(e => e.type === 'image').length
    };

    setSavedBoards([...savedBoards, newBoard]);
    setVisionBoardView('gallery');
    
    alert(`🎉 Vision Board "${currentBoard.title}" Created!\n\n✨ You've earned the Vision Creator Badge!\n💖 +25 Love Score points\n\nYour manifestation board is now saved and ready for daily visualization practice!`);
  };

  // Stock images for demo
  const stockImages = [
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200", // success
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=300&h=200", // team
    "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=300&h=200", // love
    "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=300&h=200", // home
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=200", // nature
    "https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=300&h=200"  // happiness
  ];

  const mockUser = {
    name: "Sarah",
    loveScore: 2847,
    level: 6,
    streak: 14
  };

  // Enhanced Love Garden data
  const gardenWorlds = {
    1: { name: "Blooming Garden", theme: "🌸", unlocked: true, levels: Array.from({length: 10}, (_, i) => i + 1) },
    2: { name: "Peaceful River", theme: "🌊", unlocked: false, levels: Array.from({length: 10}, (_, i) => i + 11) },
    3: { name: "Starry Sky Garden", theme: "🌙", unlocked: false, levels: Array.from({length: 10}, (_, i) => i + 21) },
    4: { name: "Mountain of Growth", theme: "⛰️", unlocked: false, levels: Array.from({length: 10}, (_, i) => i + 31) },
    5: { name: "Garden of Transformation", theme: "🦋", unlocked: false, levels: Array.from({length: 10}, (_, i) => i + 41) }
  };

  const levelDefinitions = {
    1: { name: "Gentle Start", goals: ["Match 10 ❤️ tiles"], moves: "Unlimited", difficulty: "Super Easy", reward: "10 Love Score" },
    2: { name: "Confidence Boost", goals: ["Score 500 points"], moves: "15 moves", difficulty: "Easy", reward: "10 Love Score + 10 Love Coins" },
    3: { name: "Bloom Challenge", goals: ["Match 15 🌺 tiles"], moves: "20 moves", difficulty: "Easy", reward: "15 Love Score + Bloom Badge 🌸" },
    4: { name: "Gratitude Sprout", goals: ["Match 12 💧 tiles"], moves: "15 moves", difficulty: "Easy", reward: "20 Love Score + 15 Love Coins" },
    5: { name: "Growing Strength", goals: ["Score 1,000 points"], moves: "20 moves", difficulty: "Medium", reward: "25 Love Score + 30 Love Coins + Profile Border" }
  };

  // Initialize game board
  const initializeBoard = () => {
    const board = [];
    const tiles = ['🌿', '💧', '☀️', '💖', '🔥'];
    for (let i = 0; i < 64; i++) {
      board.push({
        id: i,
        type: tiles[Math.floor(Math.random() * tiles.length)],
        selected: false
      });
    }
    setGameBoard(board);
  };

  // Handle tile click in demo
  const handleTileClick = (tileId) => {
    const newBoard = [...gameBoard];
    const tile = newBoard[tileId];
    
    // Simple demo interaction
    if (!tile.selected) {
      tile.selected = true;
      setScore(prev => prev + 10);
      
      if (tile.type === '💖') {
        setHeartsMatched(prev => prev + 1);
      } else if (tile.type === '💧') {
        setWaterMatched(prev => prev + 1);
      }
      
      setMovesLeft(prev => Math.max(0, prev - 1));
      
      // Check level completion
      const currentLevelData = levelDefinitions[selectedLevel];
      if (currentLevelData) {
        let goalsMet = false;
        
        if (currentLevelData.goals[0].includes("Match 10 ❤️") && heartsMatched >= 9) {
          goalsMet = true;
        } else if (currentLevelData.goals[0].includes("Score 500") && score >= 450) {
          goalsMet = true;
        } else if (currentLevelData.goals[0].includes("Match 12 💧") && waterMatched >= 11) {
          goalsMet = true;
        }
        
        if (goalsMet) {
          setShowLevelComplete(true);
        }
      }
    }
    
    setGameBoard(newBoard);
  };

  const startLevel = (level) => {
    setSelectedLevel(level);
    setGardenView('playing');
    setScore(0);
    setMovesLeft(levelDefinitions[level]?.moves === "Unlimited" ? 999 : parseInt(levelDefinitions[level]?.moves) || 15);
    setHeartsMatched(0);
    setWaterMatched(0);
    setShowLevelComplete(false);
    initializeBoard();
  };

  const completeLevelDemo = () => {
    const rewards = levelDefinitions[selectedLevel]?.reward || "Rewards earned!";
    
    if (selectedLevel === 3) {
      setUserBadges(prev => [...prev, 'Bloom Badge 🌸']);
    }
    
    if (levelDefinitions[selectedLevel]?.reward.includes("Love Coins")) {
      const coinsMatch = levelDefinitions[selectedLevel].reward.match(/(\d+) Love Coins/);
      if (coinsMatch) {
        setLoveCoinsBalance(prev => prev + parseInt(coinsMatch[1]));
      }
    }
    
    setShowLevelComplete(false);
    setGardenView('map');
    setCurrentLevel(prev => Math.min(50, prev + 1)); // Cap at 50 for demo, but infinite in real app
    
    alert(`🎉 Level ${selectedLevel} Complete!\n\nRewards: ${rewards}\n\n"${levelDefinitions[selectedLevel]?.name === "Gentle Start" ? "Every small step counts — you're growing." : 
      levelDefinitions[selectedLevel]?.name === "Confidence Boost" ? "See? You're stronger than you thought." :
      levelDefinitions[selectedLevel]?.name === "Bloom Challenge" ? "Like a flower, you bloom one day at a time." :
      levelDefinitions[selectedLevel]?.name === "Gratitude Sprout" ? "Gratitude waters your growth." :
      "Your effort today builds tomorrow's strength."}"`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      {/* Demo Banner */}
      <div className="bg-rose-500 text-white py-2 px-4 text-center">
        <p className="text-sm font-sans">
          📱 <strong>App Preview</strong> - See how Constant Love™ works • 
          <Link to="/signup" className="underline ml-2 font-medium">Join the Movement</Link>
        </p>
      </div>

      {/* Top Header - Centered Large Logo */}
      <div className="fixed top-8 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-rose-200 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-20 py-4 relative">
            {/* Centered Large Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/constant-love-logo.png" 
                alt="Constant Love™" 
                className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-contain hover:scale-105 transition-transform"
              />
            </Link>

            {/* Mock User Menu - Positioned Absolutely */}
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-rose-50 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  S
                </div>
                <span className="text-gray-700 font-medium hidden sm:block text-sm">{mockUser.name}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* App Content Preview - Add top padding for fixed header */}
      <div className="pb-24 pt-28">
        
        {/* Home Tab */}
        {activeTab === 'home' && (
          <div className="max-w-lg mx-auto p-4">
            {/* Welcome Header */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">Welcome back, {mockUser.name.split(' ')[0]}! 💕</h1>
              <p className="text-gray-600 font-sans">Ready to grow your Love Score today?</p>
            </div>

            {/* Love Score Dashboard - Gamified */}
            <Card className="p-6 bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 mb-6 relative overflow-hidden">
              {/* Level Badge */}
              <div className="absolute top-4 right-4">
                <div className="bg-rose-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                  <span className="text-lg">👑</span>
                  <span className="ml-1">Level {mockUser.level}</span>
                </div>
              </div>

              <div className="text-center">
                <h3 className="font-serif font-semibold text-gray-900 mb-2">Love Score Progress</h3>
                <div className="text-4xl font-bold text-rose-600 mb-2">
                  {mockUser.loveScore.toLocaleString()}
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  First Bloom • 153 points to Level 7
                </p>
                
                {/* Progress Bar with Animation */}
                <div className="bg-white rounded-full h-4 mb-4 shadow-inner">
                  <div 
                    className="bg-gradient-to-r from-rose-400 to-rose-600 h-4 rounded-full transition-all duration-1000 ease-out relative"
                    style={{width: '73%'}}
                  >
                    <div className="absolute right-0 top-0 w-4 h-4 bg-rose-600 rounded-full shadow-lg animate-pulse"></div>
                  </div>
                </div>
                
                <p className="text-xs text-rose-700 font-medium">
                  73% to Level 7 ✨
                </p>
              </div>

              {/* Floating decorative elements */}
              <div className="absolute -top-2 -left-2 w-8 h-8 bg-rose-200 rounded-full opacity-30"></div>
              <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-pink-200 rounded-full opacity-40"></div>
            </Card>

            {/* Daily Progress Stats - Gamified */}
            <Card className="p-4 bg-white/80 backdrop-blur-sm border-rose-200 mb-6">
              <h3 className="font-serif font-semibold text-gray-900 mb-3 text-center">Today's Progress</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-rose-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-rose-600">{mockUser.streak}</div>
                  <div className="text-xs text-gray-600 font-medium">Day Streak</div>
                  <div className="text-lg">🔥</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-600">3</div>
                  <div className="text-xs text-gray-600 font-medium">Activities</div>
                  <div className="text-lg">✅</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-600">+85</div>
                  <div className="text-xs text-gray-600 font-medium">Love Score</div>
                  <div className="text-lg">📈</div>
                </div>
              </div>
              
              {/* Streak Message */}
              <div className="mt-3 text-center">
                <p className="text-sm text-gray-600 font-sans italic">
                  {mockUser.streak} days strong! Keep growing 🌿
                </p>
              </div>
            </Card>

            {/* Mood Slider - Enhanced with Your Microcopy */}
            <Card className="p-6 bg-white/80 backdrop-blur-sm border-rose-200 mb-6">
              <h3 className="font-serif font-semibold text-gray-900 mb-3 text-center">How are you feeling today?</h3>
              <p className="text-sm text-gray-500 mb-4 text-center font-sans italic">
                Slide to show how you're feeling today — we'll match you with the right affirmation 💖
              </p>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center px-2">
                  {[
                    { value: 1, emoji: '😔', label: 'Struggling' },
                    { value: 2, emoji: '😐', label: 'Okay' },
                    { value: 3, emoji: '🙂', label: 'Good' },
                    { value: 4, emoji: '😊', label: 'Great' },
                    { value: 5, emoji: '🌟', label: 'Amazing' }
                  ].map((mood) => (
                    <button
                      key={mood.value}
                      className="relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 transform bg-gray-100 hover:bg-gray-200 hover:scale-110 border-2 border-gray-200 hover:border-rose-300"
                    >
                      <span className="text-xl">{mood.emoji}</span>
                    </button>
                  ))}
                </div>
                
                {/* Slider Track Visual */}
                <div className="relative mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-rose-300 h-2 rounded-full opacity-50" style={{width: '0%'}}></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>😔</span>
                    <span>😐</span>
                    <span>🙂</span>
                    <span>😊</span>
                    <span>🌟</span>
                  </div>
                </div>
                
                <div className="text-center mt-3">
                  <p className="text-sm text-gray-500 font-sans italic">
                    Select your mood above to get started ✨
                  </p>
                </div>
              </div>
            </Card>

            {/* Personalized Affirmation Section */}
            <Card className="p-6 bg-white border-2 border-rose-200 mb-6">
              <h3 className="font-serif font-semibold text-gray-900 mb-4 text-center">Your Personal Affirmation</h3>
              
              {/* Affirmation Card */}
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg p-6 mb-4 relative">
                <div className="absolute top-2 right-2">
                  <span className="text-lg">✨</span>
                </div>
                <p className="text-gray-800 font-medium italic text-center text-lg leading-relaxed">
                  "You are worthy of love, especially from yourself. Today is another chance to honor your amazing heart."
                </p>
              </div>
              
              {/* Micro Action */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-serif font-medium text-blue-900 mb-2 flex items-center">
                  <span className="text-lg mr-2">🎯</span>
                  Today's Micro Action:
                </h4>
                <p className="text-blue-800 text-sm font-sans leading-relaxed">
                  Write down one thing you appreciate about yourself and place it somewhere you'll see it today.
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button className="flex-1 text-rose-500 text-sm font-medium hover:text-rose-600 py-3 px-4 border border-rose-200 rounded-lg hover:bg-rose-50 transition-colors font-sans">
                  💕 Save to My Love Vault
                </button>
                <button className="flex-1 text-blue-500 text-sm font-medium hover:text-blue-600 py-3 px-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors font-sans">
                  ✅ Action Complete
                </button>
              </div>
              
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500 font-sans">
                  Personalized for your journey • Never repeated ✨
                </p>
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-4 bg-white/80 backdrop-blur-sm border-rose-200">
              <h3 className="font-serif font-semibold text-gray-900 mb-3 text-center">Continue Your Journey</h3>
              <div className="grid grid-cols-2 gap-3">
                <button className="py-2 px-3 border border-rose-300 text-rose-600 hover:bg-rose-50 rounded-lg font-sans text-sm">
                  🌿 Play Garden
                </button>
                <button className="py-2 px-3 border border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg font-sans text-sm">
                  📝 Reflect Now
                </button>
                <button className="py-2 px-3 border border-purple-300 text-purple-600 hover:bg-purple-50 rounded-lg font-sans text-sm">
                  🤗 Chat with Conchi
                </button>
                <button className="py-2 px-3 border border-green-300 text-green-600 hover:bg-green-50 rounded-lg font-sans text-sm">
                  👭 Find Your Sisters
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* Journal Tab */}
        {activeTab === 'journal' && (
          <div className="max-w-4xl mx-auto p-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Journaling</h2>
              <p className="text-gray-600 font-sans">Express your thoughts and discover patterns with AI insights</p>
            </div>

            {/* Journal Sub-tabs */}
            <div className="flex justify-center mb-6">
              <div className="flex space-x-1 bg-white/70 backdrop-blur-sm p-1 rounded-lg border border-rose-200">
                {[
                  { id: 'freeform', name: 'Free Form' },
                  { id: 'guided', name: 'Guided Prompts' },
                  { id: 'insights', name: 'AI Insights' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setJournalView(tab.id)}
                    className={`px-4 py-2 rounded-md font-medium transition-all font-sans ${
                      journalView === tab.id ? 'bg-rose-500 text-white' : 'text-gray-600 hover:bg-rose-50'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            {/* AI Insights View */}
            {journalView === 'insights' && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <h3 className="font-serif font-bold text-blue-900 mb-3">Mood Patterns 📊</h3>
                    <div className="text-center mb-3">
                      <div className="text-2xl font-bold text-blue-600">4.2</div>
                      <div className="text-xs text-gray-600 font-sans">Average Mood</div>
                    </div>
                    <div className="bg-white rounded-lg p-2 mb-2">
                      <p className="text-xs text-green-600 font-sans">📈 Improving +0.8</p>
                    </div>
                    <p className="text-xs text-blue-700 font-sans">
                      💡 Your mood peaks on journaling days
                    </p>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                    <h3 className="font-serif font-bold text-purple-900 mb-3">Growth Insights 🌱</h3>
                    <div className="bg-white rounded-lg p-2 mb-2">
                      <p className="text-xs font-medium text-gray-800 font-sans">Top Theme:</p>
                      <p className="text-xs text-purple-700 font-sans">Self-Acceptance</p>
                    </div>
                    <p className="text-xs text-purple-700 font-sans">
                      💡 Growing stronger through vulnerability
                    </p>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <h3 className="font-serif font-bold text-green-900 mb-3">Actions 🎯</h3>
                    <div className="text-center mb-3">
                      <div className="text-2xl font-bold text-green-600">23</div>
                      <div className="text-xs text-gray-600 font-sans">Completed</div>
                    </div>
                    <p className="text-xs text-green-700 font-sans">
                      💡 Mirror work most effective
                    </p>
                  </Card>
                </div>

                {/* Recent AI Insights */}
                <Card className="p-6 bg-white/80 border-rose-200 mb-6">
                  <h3 className="font-serif font-bold text-gray-900 mb-4">Recent AI Insights 🧠</h3>
                  <div className="space-y-3">
                    {[
                      "You're developing stronger self-worth through consistent reflection",
                      "Your mood improves on days when you practice gratitude",
                      "You're learning to reframe challenges as growth opportunities"
                    ].map((insight, index) => (
                      <div key={index} className="p-3 bg-gradient-to-r from-gray-50 to-rose-50 rounded-lg">
                        <p className="text-gray-800 font-sans text-sm">{insight}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* AI-Generated Action Plan */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Actions to Take */}
                  <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <h4 className="font-serif font-bold text-green-900 mb-3">Actions to Take 🎯</h4>
                    <p className="text-xs text-green-700 mb-4 font-sans italic">
                      AI-generated micro actions based on your journal patterns
                    </p>
                    <div className="space-y-2">
                      {[
                        "Practice mirror affirmations daily",
                        "Write in gratitude journal", 
                        "Take 5 deep breaths when stressed",
                        "Call a supportive friend"
                      ].map((action, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-white rounded-lg">
                          <input type="checkbox" className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-700 font-sans">{action}</span>
                        </div>
                      ))}
                    </div>
                    <Button size="sm" className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white rounded-full font-sans">
                      ➕ Add Action
                    </Button>
                  </Card>

                  {/* Self-Love To-Dos */}
                  <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <h4 className="font-serif font-bold text-blue-900 mb-3">Self-Love To-Dos 📋</h4>
                    <p className="text-xs text-blue-700 mb-4 font-sans italic">
                      AI-recommended daily tasks based on your growth patterns
                    </p>
                    <div className="space-y-2">
                      {[
                        "Schedule 10 minutes for yourself",
                        "Practice one affirmation",
                        "Journal about today's win",
                        "Do one thing that brings joy"
                      ].map((todo, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-white rounded-lg">
                          <input type="checkbox" className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-gray-700 font-sans">{todo}</span>
                        </div>
                      ))}
                    </div>
                    <Button size="sm" className="w-full mt-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-sans">
                      ➕ Add To-Do
                    </Button>
                  </Card>

                  {/* Stop Doing List */}
                  <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                    <h4 className="font-serif font-bold text-red-900 mb-3">Stop Doing List 🛑</h4>
                    <p className="text-xs text-red-700 mb-4 font-sans italic">
                      AI-identified negative patterns to release for your peace
                    </p>
                    <div className="space-y-2">
                      {[
                        "Comparing myself to others",
                        "Negative self-talk in mirror",
                        "Seeking validation from toxic people",
                        "Apologizing for taking up space"
                      ].map((stop, index) => (
                        <div key={index} className="flex items-center space-x-2 p-2 bg-white rounded-lg">
                          <input type="checkbox" className="w-4 h-4 text-red-500" />
                          <span className="text-sm text-gray-700 font-sans">{stop}</span>
                        </div>
                      ))}
                    </div>
                    <Button size="sm" className="w-full mt-4 bg-red-500 hover:bg-red-600 text-white rounded-full font-sans">
                      ➕ Add to Stop List
                    </Button>
                  </Card>
                </div>
              </div>
            )}

            {/* Free Form View */}
            {journalView === 'freeform' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <Card className="p-4 bg-white/80 border-rose-200">
                    <Button className="w-full bg-rose-500 hover:bg-rose-600 text-white rounded-full mb-4 font-sans">
                      ✨ New Entry
                    </Button>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block font-sans">Search Entries</label>
                        <input 
                          placeholder="Search thoughts, tags..." 
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block font-sans">Filter by Mood</label>
                        <select className="w-full p-2 border border-gray-200 rounded-lg text-sm">
                          <option>All Moods</option>
                          <option>🌟 Amazing</option>
                          <option>😊 Great</option>
                          <option>🙂 Good</option>
                          <option>😐 Okay</option>
                          <option>😔 Struggling</option>
                        </select>
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <Card className="p-3 bg-white/80 border-rose-200 text-center">
                        <div className="text-xl font-bold text-rose-600">47</div>
                        <div className="text-xs text-gray-600 font-sans">Entries</div>
                      </Card>
                      <Card className="p-3 bg-white/80 border-blue-200 text-center">
                        <div className="text-xl font-bold text-blue-600">4.2</div>
                        <div className="text-xs text-gray-600 font-sans">Avg Mood</div>
                      </Card>
                    </div>
                  </Card>
                </div>

                {/* Main Content - Writing Area */}
                <div className="lg:col-span-2">
                  {/* Writing Interface */}
                  <Card className="p-6 bg-white/80 border-rose-200 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-serif font-semibold text-gray-900">Brain Dump - Free Writing</h3>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={handleVoiceInput}
                          className={`p-2 rounded-full transition-colors ${
                            isListening ? 'bg-rose-500 text-white' : 'bg-rose-100 hover:bg-rose-200'
                          }`}
                        >
                          <span className="text-lg">🎤</span>
                        </button>
                        <span className="text-xs text-gray-500 font-sans">
                          {isListening ? 'Listening...' : 'Voice Input'}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4 font-sans italic">
                      Your journal is waiting… what's on your heart today? 💕
                    </p>
                    
                    {/* Text Area with Lines */}
                    <div className="relative">
                      <textarea
                        value={journalText}
                        onChange={(e) => setJournalText(e.target.value)}
                        placeholder="Start writing your thoughts here... or click the microphone to speak"
                        className="w-full h-64 p-4 border border-rose-200 rounded-lg resize-none focus:ring-2 focus:ring-rose-300 focus:border-rose-300 font-sans"
                        style={{
                          backgroundImage: 'repeating-linear-gradient(transparent, transparent 24px, #f1f5f9 24px, #f1f5f9 25px)',
                          lineHeight: '25px'
                        }}
                      />
                    </div>
                    
                    {/* Mood Selector for Entry */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <label className="text-sm font-medium text-gray-700 mb-2 block font-sans">How are you feeling right now?</label>
                      <div className="flex justify-center space-x-2">
                        {[
                          { emoji: '😔', label: 'Struggling' },
                          { emoji: '😐', label: 'Okay' },
                          { emoji: '🙂', label: 'Good' },
                          { emoji: '😊', label: 'Great' },
                          { emoji: '🌟', label: 'Amazing' }
                        ].map((mood, index) => (
                          <button
                            key={index}
                            className="w-10 h-10 rounded-full bg-white border border-gray-200 hover:border-rose-300 hover:bg-rose-50 flex items-center justify-center transition-all"
                          >
                            <span className="text-lg">{mood.emoji}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-between items-center mt-6">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 font-sans">Auto-saved</span>
                        <span className="text-green-500">✅</span>
                      </div>
                      <Button className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans">
                        📝 Save & Get AI Insights
                      </Button>
                    </div>
                  </Card>

                  {/* Recent Entries */}
                  <Card className="p-6 bg-white/80 border-rose-200">
                    <h3 className="font-serif font-semibold text-gray-900 mb-4">Recent Reflections</h3>
                    <div className="space-y-3">
                      {[
                        { mood: "😊", date: "Today", preview: "Had a breakthrough with Conchi about self-acceptance...", insights: 2, actions: 1 },
                        { mood: "🙂", date: "Yesterday", preview: "Practicing gratitude for my growth journey...", insights: 1, actions: 0 },
                        { mood: "😔", date: "2 days ago", preview: "Working through feelings of overwhelm...", insights: 3, actions: 2 }
                      ].map((entry, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-all">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="text-lg">{entry.mood}</span>
                              <span className="text-sm text-gray-500 font-sans">{entry.date}</span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="text-xs text-blue-600 font-sans">🧠 {entry.insights} insights</span>
                              <span className="text-xs text-green-600 font-sans">✅ {entry.actions} actions</span>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm font-sans">{entry.preview}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* Guided Prompts View */}
            {journalView === 'guided' && (
              <div>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Guided Reflection Prompts</h3>
                  <p className="text-gray-600 font-sans">Choose a prompt that resonates with your current journey</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      category: "Gratitude & Appreciation 🌸",
                      description: "Builds positive neural pathways and shifts focus from lack to abundance. Gratitude rewires your brain to notice beauty and worth.",
                      prompts: [
                        "What are three things you genuinely appreciate about yourself today?",
                        "What challenge from your past made you stronger?",
                        "Who in your life reflects your worth back to you?"
                      ],
                      color: "rose"
                    },
                    {
                      category: "Manifestation & Dreams ✨",
                      description: "Creates clarity about your desires and aligns your energy with your goals. Manifestation helps you visualize and feel your future reality.",
                      prompts: [
                        "What is one thing you're calling into your life right now, and how will it feel when it arrives?",
                        "If everything worked out perfectly, what would your life look like 12 months from now?",
                        "Write an affirmation as if your manifestation has already come true. How does it feel to live it now?"
                      ],
                      color: "purple"
                    },
                    {
                      category: "Self-Reflection 💕",
                      description: "Develops self-awareness and helps identify patterns. Reflection is the foundation of transformation and growth.",
                      prompts: [
                        "What patterns am I noticing in my thoughts lately?",
                        "How have I grown in the past month?",
                        "What would I say to my younger self about self-love?"
                      ],
                      color: "blue"
                    },
                    {
                      category: "Daily Intentions 🌿",
                      prompts: [
                        "How do I want to show up for myself today?",
                        "What act of self-love can I do right now?",
                        "What negative thought am I ready to reframe?"
                      ],
                      description: "Creates mindful awareness and intentional living. Daily intentions help you practice the NeuroLove Method™ consistently.",
                      color: "green"
                    }
                  ].map((category, categoryIndex) => (
                    <Card key={categoryIndex} className="p-6 bg-white/80 border-rose-200 hover:shadow-lg transition-all">
                      <h4 className="font-serif font-bold text-gray-900 mb-3">{category.category}</h4>
                      
                      {/* Category Description */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <p className="text-xs text-gray-700 font-sans italic">
                          <strong>Why this helps:</strong> {category.description}
                        </p>
                      </div>

                      <div className="space-y-3">
                        {category.prompts.map((prompt, promptIndex) => (
                          <button
                            key={promptIndex}
                            onClick={() => handleReflectNow(prompt, category.category, category.description)}
                            className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-rose-500 hover:bg-rose-50 transition-all"
                          >
                            <p className="text-gray-700 font-sans text-sm font-medium">{prompt}</p>
                          </button>
                        ))}
                      </div>
                      
                      <Button
                        onClick={() => {
                          // Get today's prompt (rotate daily)
                          const today = new Date().getDate();
                          const todayPrompt = category.prompts[today % category.prompts.length];
                          handleReflectNow(todayPrompt, category.category, category.description);
                        }}
                        size="sm"
                        className="w-full mt-4 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                      >
                        📝 Today's {category.category.split(' ')[0]} Prompt
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'companion' && (
          <div className="max-w-lg mx-auto p-4">
            <Card className="p-6 bg-white/80 backdrop-blur-sm border-rose-200">
              <h2 className="text-xl font-serif font-bold text-gray-900 mb-4">AI Companion</h2>
              <div className="space-y-4">
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3 max-w-xs">
                    <p className="text-sm text-gray-800 font-sans">Hey! How's your day going? 😊</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-rose-500 text-white rounded-lg p-3 max-w-xs">
                    <p className="text-sm font-sans">I'm feeling pretty good today!</p>
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg p-3 max-w-xs">
                    <p className="text-sm text-gray-800 font-sans">That's wonderful! What's bringing you joy? 💕</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'garden' && (
          <LoveGardenSimple />
        )}

        {activeTab === 'community' && (
          <div className="max-w-4xl mx-auto p-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Community</h2>
              <p className="text-gray-600 font-sans">Connect with fellow women on their transformation journey</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Channels Sidebar */}
              <div className="lg:col-span-1">
                <Card className="p-4 bg-white/80 border-rose-200">
                  <h3 className="font-serif font-semibold text-gray-900 mb-4">Channels</h3>
                  <div className="space-y-2">
                    {[
                      { name: "📢 Announcements", description: "Official updates from our team", posts: 2 },
                      { name: "💕 Daily Check-ins & Wins", description: "Share your victories", posts: 0 },
                      { name: "🌱 Growth Stories", description: "Transformation moments", posts: 0 },
                      { name: "💪 Accountability Partners", description: "Find your support buddy", posts: 0 },
                      { name: "❓ Ask the Community", description: "Get advice and support", posts: 0 }
                    ].map((channel, index) => (
                      <button
                        key={index}
                        className={`w-full text-left p-3 rounded-lg transition-colors border ${
                          index === 0 ? 'bg-rose-100 border-rose-300 text-rose-800' : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                        }`}
                      >
                        <h4 className="font-medium text-sm font-sans">{channel.name}</h4>
                        <p className="text-xs text-gray-600 mt-1 font-sans">{channel.description}</p>
                        <span className="text-xs text-gray-500 font-sans">{channel.posts} posts</span>
                      </button>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Main Content Area */}
              <div className="lg:col-span-3">
                <Card className="p-6 bg-white/80 border-rose-200">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-xl font-serif font-semibold text-gray-900">📢 Announcements</h2>
                      <p className="text-gray-600 text-sm font-sans">Official updates from the Constant Love™ team</p>
                    </div>
                    <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full font-sans">Read-Only</span>
                  </div>

                  {/* Sample Announcement Post */}
                  <div className="space-y-4">
                    <Card className="p-4 border border-gray-200 bg-gradient-to-r from-rose-50 to-pink-50">
                      <div className="flex items-start space-x-3">
                        <img 
                          src="/constant-love-logo.png" 
                          alt="Constant Love Team"
                          className="w-10 h-10 rounded-full object-contain bg-white p-1"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium text-gray-900 font-sans">Constant Love Team</span>
                            <span className="text-xs bg-rose-500 text-white px-2 py-1 rounded-full font-sans">Official</span>
                            <span className="text-xs text-gray-500 font-sans">Today</span>
                          </div>
                          <h3 className="font-serif font-semibold text-gray-900 mb-2">🌟 Welcome to the Constant Love Movement!</h3>
                          <p className="text-gray-800 font-sans text-sm leading-relaxed">
                            Welcome to our beautiful community of women supporting each other's self-love journey! 💕
                            
                            This is your safe space to share wins, ask for support, and celebrate every step of your transformation. 
                            
                            Remember: You're not alone here. We see you, we support you, and we celebrate your growth! ✨
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Empty State for Other Channels */}
                    <div className="text-center py-12">
                      <Heart className="w-12 h-12 text-rose-300 mx-auto mb-4" />
                      <h3 className="text-lg font-serif font-semibold text-gray-900 mb-2">
                        Ready to Connect?
                      </h3>
                      <p className="text-gray-600 mb-6 font-sans">
                        This channel is ready for your first post. Share your story, ask for support, or celebrate a win!
                      </p>
                      <Button className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans">
                        ✨ Share Your First Post
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Divine Guidance Tab */}
        {activeTab === 'divine' && (
          <div className="max-w-4xl mx-auto p-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Divine Guidance</h2>
              <p className="text-gray-600 font-sans">Cosmic wisdom and spiritual encouragement for your journey</p>
            </div>

            {/* Section Toggle */}
            <div className="flex justify-center mb-6">
              <div className="flex space-x-1 bg-white/80 backdrop-blur-sm p-1 rounded-xl border-2" style={{ borderColor: '#F7C6D9' }}>
                <button
                  onClick={() => setDivineSection('astrology')}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all font-serif ${
                    divineSection === 'astrology'
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-white hover:bg-rose-500'
                  }`}
                >
                  <span className="text-lg">🌟</span>
                  <span>Astrology</span>
                </button>
                <button
                  onClick={() => setDivineSection('scripture')}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all font-serif ${
                    divineSection === 'scripture'
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-white hover:bg-rose-500'
                  }`}
                >
                  <span className="text-lg">📖</span>
                  <span>Scripture</span>
                </button>
              </div>
            </div>

            {/* Astrology Section */}
            {divineSection === 'astrology' && (
              <div className="space-y-6">
                {/* Focus Area Toggle */}
                <div className="flex justify-center">
                  <div className="flex space-x-1 bg-white/70 p-1 rounded-lg border" style={{ borderColor: '#F7C6D9' }}>
                    {[
                      { id: 'general', name: 'General', icon: '🌙' },
                      { id: 'love', name: 'Love', icon: '💖' },
                      { id: 'career', name: 'Career', icon: '💼' }
                    ].map((focus) => (
                      <button
                        key={focus.id}
                        onClick={() => setAstrologyFocus(focus.id)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-all font-sans ${
                          astrologyFocus === focus.id
                            ? 'bg-rose-500 text-white shadow-md'
                            : 'text-gray-600 hover:text-white hover:bg-rose-500'
                        }`}
                      >
                        <span>{focus.icon}</span>
                        <span>{focus.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Astrology Reading */}
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                  <div className="text-center mb-6">
                    <div className="text-6xl mb-4">♌</div>
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                      Today's {astrologyFocus === 'general' ? 'Cosmic' : astrologyFocus === 'love' ? 'Love' : 'Career'} Guidance
                    </h3>
                    <p className="text-gray-600 font-sans">
                      Leo • {new Date().toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-purple-50 to-rose-50 rounded-xl p-6 border border-purple-200">
                      <h4 className="font-serif font-semibold text-gray-900 mb-3 flex items-center">
                        <span className="text-xl mr-2">🌟</span>
                        Cosmic Message for Leo
                      </h4>
                      <p className="text-gray-800 leading-relaxed font-sans text-lg">
                        {astrologyFocus === 'love' 
                          ? "Your heart is opening to new possibilities. Trust in your ability to give and receive love authentically. Today brings opportunities for deeper connection with yourself and others."
                          : astrologyFocus === 'career'
                          ? "Your natural leadership qualities are shining bright. A creative project or collaboration may present itself. Trust your instincts and step confidently into your power."
                          : "The universe is aligning to support your growth and transformation. Pay attention to synchronicities and trust your intuition. Your inner light is ready to shine even brighter."
                        }
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border border-rose-200">
                      <h4 className="font-serif font-semibold text-rose-900 mb-3 flex items-center">
                        <span className="text-xl mr-2">💖</span>
                        Your Cosmic Affirmation
                      </h4>
                      <p className="text-rose-800 italic font-medium font-sans text-lg leading-relaxed">
                        "I trust in the universe's perfect timing and embrace my radiant, authentic self."
                      </p>
                    </div>

                    <div className="flex space-x-4">
                      <Button 
                        onClick={() => alert('✨ Saved to Love Vault! Your cosmic guidance is safely stored 💕')}
                        className="flex-1 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                      >
                        💖 Save to Love Vault
                      </Button>
                      <Button 
                        onClick={() => alert('💖 Shared with Conchi! She\'s ready to discuss your cosmic insights')}
                        variant="outline"
                        className="flex-1 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white rounded-full font-sans"
                      >
                        🤗 Discuss with Conchi
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Scripture Section */}
            {divineSection === 'scripture' && (
              <div className="space-y-6">
                {/* Mood Selection */}
                <Card className="p-6 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                  <h3 className="text-lg font-serif font-semibold text-gray-900 mb-4 text-center">
                    🌸 How is your heart today?
                  </h3>
                  <p className="text-center text-gray-600 font-sans mb-4">
                    Let us find the perfect verse to encourage your spirit
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { mood: 'peaceful', emoji: '🌙', label: 'Peaceful' },
                      { mood: 'struggling', emoji: '💔', label: 'Struggling' },
                      { mood: 'grateful', emoji: '🌸', label: 'Grateful' },
                      { mood: 'anxious', emoji: '😔', label: 'Anxious' },
                      { mood: 'hopeful', emoji: '✨', label: 'Hopeful' },
                      { mood: 'overwhelmed', emoji: '🌊', label: 'Overwhelmed' },
                      { mood: 'joyful', emoji: '🌟', label: 'Joyful' },
                      { mood: 'confused', emoji: '🤔', label: 'Confused' }
                    ].map((option) => (
                      <button
                        key={option.mood}
                        onClick={() => setUserMood(option.mood)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          userMood === option.mood
                            ? 'bg-rose-500 text-white border-rose-500 shadow-lg'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-rose-500 hover:text-white hover:border-rose-500'
                        }`}
                      >
                        <div className="text-2xl mb-1">{option.emoji}</div>
                        <div className="text-sm font-sans">{option.label}</div>
                      </button>
                    ))}
                  </div>
                </Card>

                {/* Scripture Guidance */}
                <Card className="p-8 bg-white/90 backdrop-blur-sm border-2" style={{ borderColor: '#F7C6D9' }}>
                  <div className="space-y-8">
                    
                    {/* Bible Verse */}
                    <div className="text-center">
                      <div className="text-5xl mb-4">📖</div>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                        <p className="text-gray-800 font-serif text-xl leading-relaxed italic">
                          "For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future."
                        </p>
                        <p className="text-blue-600 font-sans text-sm mt-3">— Jeremiah 29:11</p>
                      </div>
                    </div>

                    {/* Interpretation */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                      <h4 className="font-serif font-semibold text-purple-900 mb-3 flex items-center">
                        <span className="text-xl mr-2">✨</span>
                        What This Means for You
                      </h4>
                      <p className="text-purple-800 font-sans text-lg leading-relaxed">
                        {userMood === 'struggling' 
                          ? "Even in your darkest moments, God sees the beautiful plan He has for your life. Your current pain is not permanent - it's preparing you for purpose."
                          : userMood === 'anxious'
                          ? "When worry fills your heart, remember that God holds your future. He has good plans for you, plans filled with hope and not harm."
                          : "God's love for you is intentional and purposeful. Trust that He is working everything together for your good, even when you can't see the full picture."
                        }
                      </p>
                    </div>

                    {/* Action Step */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                      <h4 className="font-serif font-semibold text-green-900 mb-3 flex items-center">
                        <span className="text-xl mr-2">🪞</span>
                        Today's Action Step
                      </h4>
                      <p className="text-green-800 font-sans text-lg leading-relaxed">
                        Write down one dream that feels scary but exciting. Pray over it and ask God to guide your next small step toward it.
                      </p>
                    </div>

                    {/* Prayer */}
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6 border border-yellow-200">
                      <h4 className="font-serif font-semibold text-yellow-900 mb-3 flex items-center">
                        <span className="text-xl mr-2">🙏</span>
                        Personalized Prayer
                      </h4>
                      <p className="text-yellow-800 font-sans text-lg leading-relaxed italic">
                        "Dear God, help me trust in Your perfect timing and plan for my life. Give me courage to take the next step and faith to believe that You are working everything together for my good. Amen."
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-4">
                      <Button 
                        onClick={() => alert('✨ Scripture saved to Love Vault! A reminder of God\'s love, always waiting for you 💕')}
                        className="flex-1 bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                      >
                        💖 Save to Love Vault
                      </Button>
                      <Button 
                        onClick={() => alert('🙏 Shared with Conchi! She\'s ready to discuss how this verse speaks to your heart')}
                        variant="outline"
                        className="flex-1 border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white rounded-full font-sans"
                      >
                        🤗 Discuss with Conchi
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Vision Board Tab */}
        {activeTab === 'vision' && (
          <div className="max-w-6xl mx-auto p-4">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-4">
                <span className="text-3xl mr-3">🎨</span>
                <h2 className="text-3xl font-serif font-bold text-gray-900">Vision Board</h2>
              </div>
              <p className="text-gray-600 font-sans text-lg">Create visual manifestation boards for your dreams and goals</p>
            </div>

            {/* Vision Board Gallery */}
            <div className="space-y-8">
              
              {/* Your Vision Boards Section */}
              <Card className="p-8 bg-white/90 backdrop-blur-sm border-2 border-rose-200">
                <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6 text-center">Your Vision Boards</h3>
                
                {/* Empty State - Encourage First Board Creation */}
                <div className="text-center py-12">
                  <div className="text-8xl mb-6">✨</div>
                  <h4 className="text-xl font-serif font-bold text-gray-900 mb-4">
                    Your manifestation journey starts here
                  </h4>
                  <p className="text-gray-600 font-sans mb-8 max-w-2xl mx-auto text-lg">
                    Create your first vision board to visualize your dreams and align your energy with your goals. 
                    The universe responds to clarity and intention! 💫
                  </p>
                  
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 mb-8 max-w-xl mx-auto">
                    <h5 className="font-serif font-semibold text-rose-900 mb-3">🌸 Why Vision Boards Work:</h5>
                    <div className="space-y-2 text-sm text-rose-800 font-sans">
                      <p>• <strong>Reflect:</strong> Clarify what you truly desire</p>
                      <p>• <strong>Reframe:</strong> Visualize positive outcomes</p> 
                      <p>• <strong>Rewire:</strong> Create new neural pathways for success</p>
                      <p>• <strong>Reinforce:</strong> Daily visualization builds belief</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                    <Button 
                      onClick={() => {
                        alert('🎨 Manual Editor Demo\n\n• Upload your own photos\n• Drag and drop to arrange\n• Add personal affirmations\n• Full creative control\n\nFeature: Manual creation mode activated!');
                      }}
                      className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans text-lg py-4"
                    >
                      <span className="text-2xl mr-2">🎨</span>
                      Create Manual Board
                    </Button>
                    
                    <Button 
                      onClick={() => {
                        alert('✨ AI-Assisted Demo\n\n• Answer guided prompts\n• AI generates beautiful layout\n• Instant vision board creation\n• Perfect for beginners\n\nFeature: AI creation mode activated!');
                      }}
                      className="bg-purple-500 hover:bg-purple-600 text-white rounded-full font-sans text-lg py-4"
                    >
                      <span className="text-2xl mr-2">✨</span>
                      AI-Assisted Creation
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Creation Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card className="p-8 bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 hover:shadow-xl transition-all">
                  <div className="text-center">
                    <div className="text-8xl mb-6">🎨</div>
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">Manual Editor</h3>
                    <p className="text-gray-600 font-sans mb-6 text-lg">
                      Upload your own photos, arrange images, and add personal affirmations to create your perfect vision board
                    </p>
                    
                    <div className="bg-white/80 rounded-lg p-4 mb-6">
                      <h4 className="font-serif font-semibold text-rose-900 mb-3">✨ Features:</h4>
                      <div className="space-y-2 text-sm text-rose-800 font-sans">
                        <p>• Upload your own images or choose from library</p>
                        <p>• Drag & drop to arrange elements</p>
                        <p>• Add affirmations and goal text</p>
                        <p>• Customize colors and layouts</p>
                        <p>• Save multiple boards</p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => {
                        toast({
                          title: "🎨 Manual Editor Demo",
                          description: "Full drag-and-drop vision board creator with image uploads and text editing",
                          duration: 4000,
                        });
                      }}
                      className="w-full bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans text-lg py-3"
                    >
                      🎨 Try Manual Editor
                    </Button>
                  </div>
                </Card>

                <Card className="p-8 bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 hover:shadow-xl transition-all">
                  <div className="text-center">
                    <div className="text-8xl mb-6">✨</div>
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">AI-Assisted Creation</h3>
                    <p className="text-gray-600 font-sans mb-6 text-lg">
                      Answer guided prompts about your dreams and let AI create a beautiful, personalized vision board for you
                    </p>
                    
                    <div className="bg-white/80 rounded-lg p-4 mb-6">
                      <h4 className="font-serif font-semibold text-purple-900 mb-3">🤖 AI Features:</h4>
                      <div className="space-y-2 text-sm text-purple-800 font-sans">
                        <p>• Answer manifestation prompts</p>
                        <p>• AI selects perfect images</p>
                        <p>• Automatic layout generation</p>
                        <p>• Personalized affirmations</p>
                        <p>• Instant beautiful results</p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => {
                        toast({
                          title: "✨ AI-Assisted Demo", 
                          description: "Guided prompts with AI-generated vision boards using your manifestation goals",
                          duration: 4000,
                        });
                      }}
                      className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full font-sans text-lg py-3"
                    >
                      ✨ Try AI Creation
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Sample Demo Boards (After Creating) */}
              <Card className="p-6 bg-white border-2 border-rose-200">
                <h3 className="text-xl font-serif font-bold text-gray-900 mb-6">📋 Sample Vision Boards</h3>
                <p className="text-center text-gray-600 font-sans mb-6">
                  Here's what your vision boards could look like after creating them:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { 
                      title: "My 2025 Dreams", 
                      category: "Life Goals", 
                      description: "Career success, loving relationships, financial abundance",
                      created: "Would show after creation",
                      color: "from-yellow-100 to-yellow-200"
                    },
                    { 
                      title: "Love & Relationships", 
                      category: "Heart Desires", 
                      description: "Manifesting deep connection and self-love",
                      created: "Would show after creation",
                      color: "from-pink-100 to-pink-200"
                    },
                    { 
                      title: "Dream Career", 
                      category: "Professional", 
                      description: "Purpose-driven work and creative fulfillment",
                      created: "Would show after creation",
                      color: "from-blue-100 to-blue-200"
                    }
                  ].map((board, index) => (
                    <Card key={index} className={`p-6 bg-gradient-to-br ${board.color} border-2 border-gray-200 hover:shadow-lg transition-shadow`}>
                      <div className="aspect-video bg-white/80 rounded-lg mb-4 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <div className="text-center text-gray-500">
                          <div className="text-4xl mb-2">🖼️</div>
                          <p className="text-sm font-sans">Vision Board Preview</p>
                        </div>
                      </div>
                      <h4 className="font-serif font-semibold text-gray-900 mb-2">{board.title}</h4>
                      <p className="text-gray-600 text-sm font-sans mb-2">{board.description}</p>
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-xs text-gray-500 font-sans">{board.created}</span>
                        <div className="space-x-2">
                          <Button 
                            size="sm" 
                            onClick={() => {
                              toast({
                                title: "👁️ Viewing Vision Board",
                                description: `Opening ${board.title} for daily manifestation practice`,
                                duration: 3000,
                              });
                            }}
                            variant="outline"
                            className="border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white rounded-full font-sans"
                          >
                            👁️ View
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => {
                              toast({
                                title: "✏️ Editing Vision Board",
                                description: `Opening ${board.title} in edit mode`,
                                duration: 3000,
                              });
                            }}
                            className="bg-rose-500 hover:bg-rose-600 text-white rounded-full font-sans"
                          >
                            ✏️ Edit
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>

              {/* Manifestation Habit Integration */}
              <Card className="p-8 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300">
                <div className="text-center">
                  <div className="text-6xl mb-4">🌟</div>
                  <h3 className="text-2xl font-serif font-bold text-yellow-900 mb-4">Daily Manifestation Habit</h3>
                  <p className="text-yellow-800 font-sans mb-6 text-lg">
                    Visit your vision boards daily to reinforce your manifestations and keep your dreams alive in your heart.
                    Consistent visualization rewires your brain for success! 
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/80 rounded-lg p-4">
                      <div className="text-2xl mb-2">🎯</div>
                      <h4 className="font-serif font-semibold text-gray-900 mb-1">Daily Practice</h4>
                      <p className="text-xs text-gray-600 font-sans">View boards for 5 minutes each morning</p>
                    </div>
                    <div className="bg-white/80 rounded-lg p-4">
                      <div className="text-2xl mb-2">💖</div>
                      <h4 className="font-serif font-semibold text-gray-900 mb-1">Feel the Emotions</h4>
                      <p className="text-xs text-gray-600 font-sans">Imagine how achieving your dreams will feel</p>
                    </div>
                    <div className="bg-white/80 rounded-lg p-4">
                      <div className="text-2xl mb-2">🔄</div>
                      <h4 className="font-serif font-semibold text-gray-900 mb-1">Update Regularly</h4>
                      <p className="text-xs text-gray-600 font-sans">Add new dreams as you grow and evolve</p>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => {
                      toast({
                        title: "🌟 Daily Manifestation Practice",
                        description: "Reminder set! We'll encourage you to visualize your dreams daily",
                        duration: 4000,
                      });
                    }}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-full font-sans text-lg px-8 py-3"
                  >
                    ✨ Set Daily Reminder
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'more' && (
          <div className="max-w-lg mx-auto p-4">
            <Card className="p-6 bg-white/80 backdrop-blur-sm border-rose-200">
              <h2 className="text-xl font-serif font-bold text-gray-900 mb-4 text-center">More Features</h2>
              
              {/* Main Features */}
              <div className="space-y-4 mb-6">
                <button 
                  onClick={() => setActiveTab('divine')}
                  className="w-full p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🔮</span>
                    <div className="text-left">
                      <h3 className="font-serif font-semibold text-gray-900">Divine Guidance</h3>
                      <p className="text-gray-600 text-sm font-sans">Astrology insights and spiritual scripture</p>
                    </div>
                  </div>
                </button>

                <button 
                  onClick={() => setActiveTab('vision')}
                  className="w-full p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg border border-pink-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">🎨</span>
                    <div className="text-left">
                      <h3 className="font-serif font-semibold text-gray-900">Vision Board</h3>
                      <p className="text-gray-600 text-sm font-sans">Create visual manifestation boards</p>
                    </div>
                  </div>
                </button>
                
                <button 
                  onClick={() => alert('Love Vault feature - Save your favorite affirmations and moments!')}
                  className="w-full p-4 bg-gradient-to-r from-rose-50 to-rose-100 rounded-lg border border-rose-200 hover:shadow-lg transition-all"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">💖</span>
                    <div className="text-left">
                      <h3 className="font-serif font-semibold text-gray-900">Love Vault</h3>
                      <p className="text-gray-600 text-sm font-sans">Your saved affirmations and milestones</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Support */}
              <h3 className="font-serif font-semibold text-gray-900 mb-3">Support & Resources</h3>
              <div className="space-y-2">
                <button className="w-full py-3 px-4 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg font-sans text-left">
                  🆘 Help Center
                </button>
                <button className="w-full py-3 px-4 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg font-sans text-left">
                  🔒 Crisis Support
                </button>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 font-sans italic">
                  💕 Profile Settings available in top-right menu
                </p>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Journal Modal for Guided Prompts */}
      {showJournalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-white">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif font-bold text-gray-900">Guided Reflection</h3>
                <button 
                  onClick={() => setShowJournalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {selectedPrompt && (
                <div>
                  <div className="bg-rose-50 rounded-lg p-4 mb-4">
                    <h4 className="font-serif font-semibold text-gray-900 mb-2">{selectedPrompt.category}</h4>
                    <p className="text-rose-800 font-sans text-sm italic mb-3">{selectedPrompt.description}</p>
                    <p className="text-gray-800 font-sans font-medium">{selectedPrompt.prompt}</p>
                  </div>

                  <div className="relative mb-4">
                    <textarea
                      value={journalText}
                      onChange={(e) => setJournalText(e.target.value)}
                      placeholder="Start reflecting on this prompt... click the microphone to speak your thoughts"
                      className="w-full h-48 p-4 border border-rose-200 rounded-lg resize-none focus:ring-2 focus:ring-rose-300 font-sans"
                      style={{
                        backgroundImage: 'repeating-linear-gradient(transparent, transparent 24px, #f1f5f9 24px, #f1f5f9 25px)',
                        lineHeight: '25px'
                      }}
                    />
                    <button 
                      onClick={handleVoiceInput}
                      className={`absolute bottom-4 right-4 p-2 rounded-full transition-colors ${
                        isListening ? 'bg-rose-500 text-white animate-pulse' : 'bg-rose-100 hover:bg-rose-200'
                      }`}
                    >
                      <span className="text-lg">🎤</span>
                    </button>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 font-sans">
                        {isListening ? 'Listening... speak your thoughts 🎤' : 'Click microphone for voice input'}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={() => setShowJournalModal(false)}
                        variant="outline" 
                        className="border-gray-300 text-gray-600 font-sans"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowJournalModal(false);
                          alert('📝 Your reflection is safe. Keep going, you\'re rewiring daily 💕');
                        }}
                        className="bg-rose-500 hover:bg-rose-600 text-white font-sans"
                      >
                        💕 Save Reflection
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Bottom Navigation Preview */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-rose-200 z-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-6 gap-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex flex-col items-center px-1 py-2 text-xs font-medium transition-all duration-300 ${
                    activeTab === item.id
                      ? 'text-white bg-rose-500'
                      : 'text-gray-600 hover:text-white hover:bg-rose-500'
                  }`}
                >
                  <Icon className="w-5 h-5 mb-0.5" />
                  <span className="leading-none text-center" style={{ fontSize: '10px' }}>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppPreview;